import { Options } from "@contentful/rich-text-react-renderer";
import { BLOCKS } from "@contentful/rich-text-types";
import cx from "classnames";
import { readFragment, ResultOf } from "gql.tada";
import { ReactNode } from "react";
import {
  SolutionsChapterFragment,
  SolutionsRowFragment,
} from "@queries/entries/SolutionsSectionById";
import { IconGridFragment } from "@queries/fragments/IconGridFragment";
import { LinkFragment } from "@queries/fragments/LinkFragment";
import { RichText } from "@components/shared/core/RichText";
import { MediaItem } from "@components/shared/MediaItem";
import { Button } from "@components/shared/core/Button";
import { Link } from "@components/shared/core/Link";
import SolutionsChapterRows from "./SolutionsChapterRows";
import cn from "classnames";
import { MarkdownLite } from "@components/shared/MarkdownLite";
import { useTranslation } from "@services/i18n";
import { OBELIX_CTA_KEYS } from "@utils/obelixKeys";

const SolutionsChapterHeading = ({ children }: { children: ReactNode }) =>
  children && <h4 className="text-heading-4 mb-[22px]">{children}</h4>;

const mediaSourceOptions = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_, children) => (
      <p className="text-label font-normal text-brand-cream">{children}</p>
    ),
  },
} satisfies Options;

const IconGridItems = ({
  iconGrid,
}: {
  iconGrid: ResultOf<typeof IconGridFragment> | null;
}) => {
  if (!iconGrid?.iconsCollection?.items?.length) return null;
  return (
    <div className="grid grid-cols-2 dsk:grid-cols-4">
      {iconGrid.iconsCollection.items.map((icon) => (
        <div
          key={icon?._id}
          className="w-[123px] dsk:w-[158px] aspect-square flex justify-center items-center"
        >
          {icon?.asset && (
            <MediaItem item={icon.asset} className="w-full h-auto" width={160} />
          )}
        </div>
      ))}
    </div>
  );
};

const SolutionsChapter = ({
  chapter,
  index,
}: {
  chapter: ResultOf<typeof SolutionsChapterFragment>;
  index: number;
}) => {
  const rows = (chapter.rowsCollection?.items ?? [])
    .map((item) => readFragment(SolutionsRowFragment, item))
    .filter(Boolean);
  const iconGrid = chapter.iconGrid
    ? readFragment(IconGridFragment, chapter.iconGrid)
    : null;
  const ctaLink = chapter.ctaLink
    ? readFragment(LinkFragment, chapter.ctaLink)
    : null;
  const { t } = useTranslation();

  const hasMedia = Boolean(chapter.media || chapter.mediaSourceRichText);

  return (
    <div
      className={cn("flex flex-col dsk:mb-[100px] mb-[50px]", {
        "mt-20": index !== 0,
      })}
    >
      <div className="flex flex-col gap-10">
        {chapter.eyebrow && (
          <p className="text-body-large text-brand-cream">{chapter.eyebrow}</p>
        )}
        {chapter.title && (
          <h2 className="text-heading-1 w-full">
            <MarkdownLite>{chapter.title}</MarkdownLite>
          </h2>
        )}
      </div>
      {(chapter.media || chapter.mediaHeading || chapter.mediaBodyRichText || iconGrid) && (
        <div className="mt-20">
          {chapter.mediaLayout?.includes("Vertical") ? (
            <div
              className={cx(
                "mb-10 dsk:mt-10 flex flex-col gap-[43px]",
                chapter.mediaLayout === "Vertical (media on bottom)" &&
                  "flex-col-reverse"
              )}
            >
              {hasMedia && (
                <div className="flex flex-col items-start gap-2">
                  <MediaItem
                    item={chapter.media}
                    className="w-full rounded-xl"
                    width={1000}
                  />
                  <RichText
                    content={chapter.mediaSourceRichText}
                    options={mediaSourceOptions}
                  />
                </div>
              )}

              {(chapter.mediaHeading || chapter.mediaBodyRichText || iconGrid) && (
                <div className="w-full flex flex-col">
                  <SolutionsChapterHeading>
                    <MarkdownLite>{chapter.mediaHeading ?? ""}</MarkdownLite>
                  </SolutionsChapterHeading>
                  <div className="text-brand-cream [&>*:last-child]:mb-0">
                    <RichText content={chapter.mediaBodyRichText} />
                  </div>
                  {ctaLink && (
                    <Button asChild variant="light-on-blur" className="mt-[40px] self-start">
                      <Link link={ctaLink}>{ctaLink.text ?? t(OBELIX_CTA_KEYS.learnMore.obelixKey, OBELIX_CTA_KEYS.learnMore.label)}</Link>
                    </Button>
                  )}
                  <IconGridItems iconGrid={iconGrid} />
                </div>
              )}
            </div>
          ) : (
            <div
              className={cx(
                chapter.mediaLayout,
                "mb-10 dsk:mt-10 flex flex-col gap-[43px]",
                "dsk:grid dsk:grid-cols-[repeat(17,_minmax(0,_1fr))] dsk:gap-x-4 dsk:grid-flow-row-dense"
              )}
            >
              <div
                className={cx(
                  "flex flex-col dsk:col-span-6 dsk:justify-center",
                  {
                    "dsk:col-start-12":
                      chapter.mediaLayout === "Horizontal (media on left)",
                  }
                )}
              >
                <SolutionsChapterHeading>
                  <MarkdownLite>{chapter.mediaHeading ?? ""}</MarkdownLite>
                </SolutionsChapterHeading>
                <div className="text-brand-cream [&>*:last-child]:mb-0">
                  <RichText content={chapter.mediaBodyRichText} />
                </div>
                {ctaLink && (
                  <Button asChild variant="light-on-blur" className="mt-[40px] self-start">
                    <Link link={ctaLink}>{ctaLink.text ?? t(OBELIX_CTA_KEYS.learnMore.obelixKey, OBELIX_CTA_KEYS.learnMore.label)}</Link>
                  </Button>
                )}
                <IconGridItems iconGrid={iconGrid} />
              </div>
              <div
                className={cx(
                  "flex flex-col items-start gap-2 dsk:col-span-9",
                  chapter.mediaLayout === "Horizontal (media on left)"
                    ? "dsk:col-start-1"
                    : "dsk:col-start-9"
                )}
              >
                <MediaItem
                  item={chapter.media}
                  className="w-full rounded-xl"
                  width={1000}
                />
                <RichText
                  content={chapter.mediaSourceRichText}
                  options={mediaSourceOptions}
                />
              </div>
            </div>
          )}
        </div>
      )}
      <SolutionsChapterRows
        rows={rows!}
        hasMedia={Boolean(
          chapter.mediaHeading ||
            chapter.media ||
            chapter.mediaBodyRichText ||
            chapter.mediaSourceRichText
        )}
      />
    </div>
  );
};

export default SolutionsChapter;
